import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  CheckCircleIcon,
  XMarkIcon,
  DocumentTextIcon,
  ClockIcon,
} from "@heroicons/react/24/outline";

const SupportTicketModal = ({ isOpen, onClose, ticketData }) => {
  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4 text-center">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-25 transition-opacity"
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.2 }}
            className="relative transform overflow-hidden rounded-2xl bg-white text-left shadow-xl transition-all w-full max-w-md"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-c-blue to-[#295F92] px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <CheckCircleIcon className="h-8 w-8 text-white" />
                  <h3 className="text-lg font-semibold text-white">
                    Support Ticket Submitted
                  </h3>
                </div>
                <button
                  onClick={onClose}
                  className="rounded-full p-1 text-white hover:bg-white hover:bg-opacity-20 transition-colors"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="px-6 py-6">
              <div className="space-y-4">
                {/* Success Message */}
                <div className="text-center">
                  <p className="text-gray-600 mb-4">
                    Your support ticket has been successfully submitted. We'll
                    get back to you within 24 hours.
                  </p>
                </div>

                {/* Ticket Details */}
                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                  <div className="flex items-center gap-2">
                    <DocumentTextIcon className="h-5 w-5 text-c-blue" />
                    <span className="font-semibold text-gray-900">
                      Ticket Details
                    </span>
                  </div>

                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Ticket ID:</span>
                      <span className="font-mono text-gray-900">
                        {ticketData?.ticketId || "N/A"}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Name:</span>
                      <span className="text-gray-900">{ticketData?.name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Email:</span>
                      <span className="text-gray-900">{ticketData?.email}</span>
                    </div>
                    {ticketData?.company && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Company:</span>
                        <span className="text-gray-900">
                          {ticketData.company}
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-gray-600">Subject:</span>
                      <span className="text-gray-900">
                        {ticketData?.subject}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Next Steps */}
                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <ClockIcon className="h-5 w-5 text-blue-600 mt-0.5" />
                    <div>
                      <h4 className="font-semibold text-blue-900 mb-1">
                        What happens next?
                      </h4>
                      <ul className="text-sm text-blue-800 space-y-1">
                        <li>• You'll receive a confirmation email</li>
                        <li>• Our support team will review your ticket</li>
                        <li>• We'll respond within 24 hours</li>
                        <li>• You can track progress via email updates</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="bg-gray-50 px-6 py-4 flex justify-end">
              <button
                onClick={onClose}
                className="bg-c-blue text-white px-6 py-2 rounded-lg font-semibold hover:bg-[#295F92] transition-colors"
              >
                Close
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    </AnimatePresence>
  );
};

export default SupportTicketModal;
